import React, { useCallback, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import AuthService from '../../services/authService';
import {
  createStaffCourseMapping,
  getStaffCourseMappingOptions,
  updateStaffCourseMapping,
} from '../../services/staffCourseMappingService';

const ACCEPTED_EXTENSIONS = ['xlsx', 'xls', 'csv'];
const HEADER_VARIANTS = {
  staffId: ['staffid', 'staffidrequired', 'staffidoptional', 'staffidrequired', 'staffid'],
  courseCodeId: ['coursecodeid', 'coursecodeidrequired', 'coursecodeidoptional'],
  monthNumber: [
    'assignmentmonthnumber112',
    'assignmentmonthnumber112required',
    'assignmentmonth',
    'month',
  ],
  year: ['assignmentyearyyyy', 'assignmentyear', 'year', 'assignmentyearyyyyrequired'],
};

const STATUS_STYLES = {
  pending: 'bg-slate-100 text-slate-800',
  uploading: 'bg-amber-100 text-amber-800',
  success: 'bg-emerald-100 text-emerald-900 font-semibold border border-emerald-200',
  error: 'bg-rose-100 text-rose-700',
  blocked: 'bg-rose-50 text-rose-600',
  skipped: 'bg-slate-50 text-slate-500',
};

const sanitizeText = (value) => (value === undefined || value === null ? '' : String(value).trim());

const normalizeNumber = (value) => {
  const text = sanitizeText(value);
  if (!text) return null;
  const numeric = Number(text);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatMonthKey = (year, monthNumber) => {
  if (!year || !monthNumber) return null;
  if (!Number.isInteger(year) || String(year).length !== 4) return null;
  if (!Number.isInteger(monthNumber) || monthNumber < 1 || monthNumber > 12) return null;
  return `${year}-${String(monthNumber).padStart(2, '0')}`;
};

const buildRow = (record, rowNumber, lookups, duplicateTracker) => {
  const staffId = normalizeNumber(record.staffId);
  const courseCodeId = normalizeNumber(record.courseCodeId);
  const monthNumber = normalizeNumber(record.monthNumber);
  const year = normalizeNumber(record.year);

  const errors = [];

  if (!staffId) errors.push('Staff ID is required');
  if (staffId && !lookups.staffIds.has(staffId)) errors.push('Staff ID not found');

  if (!courseCodeId) errors.push('Course Code ID is required');
  if (courseCodeId && !lookups.courseIds.has(courseCodeId)) errors.push('Course Code ID not found');

  if (!year || String(year).length !== 4) errors.push('Year must be YYYY');
  if (!monthNumber || monthNumber < 1 || monthNumber > 12) errors.push('Month must be between 1 and 12');

  const monthKey = formatMonthKey(year, monthNumber);
  const duplicateKey = staffId && courseCodeId && monthKey ? `${staffId}-${courseCodeId}-${monthKey}` : null;

  if (duplicateKey) {
    if (duplicateTracker.has(duplicateKey)) {
      errors.push('Duplicate row in file');
    } else {
      duplicateTracker.add(duplicateKey);
    }
    // Existing mappings in the database are handled during upload
    // (skip or update), so we don't block rows here based on them.
  }

  const payload = staffId && courseCodeId && monthKey
    ? { staffId: Number(staffId), courseCodeId: Number(courseCodeId), monthKey }
    : null;

  return {
    rowNumber,
    preview: {
      staffId: staffId || '',
      courseCodeId: courseCodeId || '',
      monthNumber: monthNumber || '',
      year: year || '',
    },
    payload,
    errors,
    status: errors.length ? 'blocked' : 'pending',
    message: errors.length ? 'Resolve the validation issues before uploading.' : '',
  };
};

export default function UploadStaffCourseMappings() {
  const fileInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [fileMeta, setFileMeta] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [options, setOptions] = useState({
    staffIds: new Set(),
    courseIds: new Set(),
    existingAssignments: new Set(),
    assignmentIndexByFullKey: new Map(),
    assignmentIndexByCoursePeriod: new Map(),
  });
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const user = AuthService.getUser();
  const hasAdminAccess = Boolean(
    user?.superAdmin?.superAdminId || user?.superAdmin?.staff?.type === 'admin'
  );

  const readyRows = useMemo(() => rows.filter((row) => row.errors.length === 0), [rows]);
  const blockedRows = useMemo(() => rows.filter((row) => row.errors.length > 0), [rows]);
  const successCount = useMemo(() => rows.filter((row) => row.status === 'success').length, [rows]);
  const failedCount = useMemo(() => rows.filter((row) => row.status === 'error').length, [rows]);

  const patchRow = useCallback((rowNumber, updates) => {
    setRows((prev) => prev.map((row) => (row.rowNumber === rowNumber ? { ...row, ...updates } : row)));
  }, []);

  const resetWorkspace = useCallback(() => {
    setRows([]);
    setFileMeta(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const loadOptions = useCallback(async () => {
    try {
      const data = await getStaffCourseMappingOptions();
      const staffIds = new Set((data?.staff || []).map((item) => Number(item.id)).filter(Number.isFinite));
      const courseIds = new Set((data?.courseCodes || []).map((item) => Number(item.id)).filter(Number.isFinite));

      const existingAssignments = new Set();
      const assignmentIndexByFullKey = new Map();
      const assignmentIndexByCoursePeriod = new Map();

      (data?.assignments || []).forEach((item) => {
        const staffId = Number(item.staffId);
        const courseCodeId = Number(item.courseCodeId);
        const id = Number(item.id);
        const monthKey = item.monthKey;
        if (!staffId || !courseCodeId || !monthKey || !id) return;

        const fullKey = `${staffId}-${courseCodeId}-${monthKey}`;
        const coursePeriodKey = `${courseCodeId}-${monthKey}`;

        const assignment = { id, staffId, courseCodeId, monthKey };

        existingAssignments.add(fullKey);
        assignmentIndexByFullKey.set(fullKey, assignment);
        if (!assignmentIndexByCoursePeriod.has(coursePeriodKey)) {
          assignmentIndexByCoursePeriod.set(coursePeriodKey, assignment);
        }
      });

      const built = {
        staffIds,
        courseIds,
        existingAssignments,
        assignmentIndexByFullKey,
        assignmentIndexByCoursePeriod,
      };
      setOptions(built);
      setOptionsLoaded(true);
      return built;
    } catch (error) {
      console.error('Failed to load staff/course options', error);
      Swal.fire('Unable to load options', 'Please refresh the page and try again.', 'error');
      return null;
    }
  }, []);

  const normalizeHeaderKey = (text) => sanitizeText(text).toLowerCase().replace(/[^a-z0-9]/g, '');

  const parseMatrix = (matrix) => {
    if (!matrix.length) {
      throw new Error('The worksheet is empty.');
    }

    const headerRowRaw = matrix[0] || [];
    const headerRow = headerRowRaw.map((cell) => sanitizeText(cell));
    const headerKeyMap = headerRow.map((cell) => {
      const normalized = normalizeHeaderKey(cell);
      if (HEADER_VARIANTS.staffId.includes(normalized)) return 'staffId';
      if (HEADER_VARIANTS.courseCodeId.includes(normalized)) return 'courseCodeId';
      if (HEADER_VARIANTS.monthNumber.includes(normalized)) return 'monthNumber';
      if (HEADER_VARIANTS.year.includes(normalized)) return 'year';
      return null;
    });

    const requiredKeys = ['staffId', 'courseCodeId', 'monthNumber', 'year'];
    const missing = requiredKeys.find((key) => !headerKeyMap.includes(key));
    if (missing) {
      throw new Error('Missing required columns: Staff ID, Course Code ID, Assignment Month, Assignment Year.');
    }

    const dataRows = matrix.slice(1);
    if (!dataRows.length) {
      throw new Error('No data rows were found beneath the header.');
    }

    const rowsAsObjects = dataRows.map((rowArray) => {
      const rowObject = { staffId: '', courseCodeId: '', monthNumber: '', year: '' };
      headerKeyMap.forEach((key, idx) => {
        if (!key) return;
        rowObject[key] = rowArray[idx] ?? '';
      });
      return rowObject;
    });

    return rowsAsObjects;
  };

  const processFile = useCallback(async (file) => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !ACCEPTED_EXTENSIONS.includes(extension)) {
      Swal.fire('Unsupported file', 'Please upload a .xlsx, .xls, or .csv file.', 'warning');
      return;
    }

    let lookups = options;
    if (!optionsLoaded) {
      const fresh = await loadOptions();
      if (fresh) {
        lookups = fresh;
      }
    }

    setParsing(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      if (!workbook.SheetNames?.length) {
        throw new Error('This workbook does not contain any sheets.');
      }

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const matrix = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false, blankrows: false });
      const rowsAsObjects = parseMatrix(matrix);

      const duplicateTracker = new Set();
      const parsedRows = rowsAsObjects
        .map((row, index) => buildRow(row, index + 2, lookups, duplicateTracker))
        .filter((row) => !(row.errors.length === 0 && !row.payload));

      if (!parsedRows.length) {
        throw new Error('No usable rows were found in the sheet.');
      }

      setRows(parsedRows);
      setFileMeta({ name: file.name, sheet: sheetName, total: parsedRows.length, importedAt: new Date() });

      const issues = parsedRows.filter((row) => row.errors.length > 0).length;
      if (issues > 0) {
        Swal.fire('File parsed with issues', `${parsedRows.length} rows loaded, ${issues} need fixes.`, 'warning');
      } else {
        Swal.fire('File ready', `${parsedRows.length} row(s) parsed successfully.`, 'success');
      }
    } catch (error) {
      console.error('Staff course mapping template parsing failed:', error);
      setRows([]);
      setFileMeta(null);
      Swal.fire('Unable to read file', error.message || 'Please double-check the template.', 'error');
    } finally {
      setParsing(false);
    }
  }, [loadOptions, options, optionsLoaded]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      await processFile(file);
      event.target.value = '';
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  const handleUpload = async () => {
    if (!readyRows.length) {
      Swal.fire('No valid rows', 'Please fix the highlighted issues before uploading.', 'info');
      return;
    }

    setUploading(true);
    let success = 0;
    let failure = 0;

    const snapshot = [...rows];
    for (const row of snapshot) {
      if (row.errors.length > 0 || !row.payload) {
        patchRow(row.rowNumber, { status: 'blocked', message: 'Fix validation issues in the sheet.' });
        continue;
      }

      const { staffId, courseCodeId, monthKey } = row.payload;
      const fullKey = `${staffId}-${courseCodeId}-${monthKey}`;
      const coursePeriodKey = `${courseCodeId}-${monthKey}`;

      const exactAssignment = options.assignmentIndexByFullKey?.get(fullKey);
      if (exactAssignment) {
        // Same Staff ID, Course Code ID, Month, Year already exists
        patchRow(row.rowNumber, { status: 'skipped', message: 'Already exist data.' });
        continue;
      }

      const periodAssignment = options.assignmentIndexByCoursePeriod?.get(coursePeriodKey);
      if (periodAssignment) {
        if (periodAssignment.staffId !== staffId) {
          // Same Course Code ID + Month/Year, but Staff ID changed -> update staff
          patchRow(row.rowNumber, { status: 'uploading', message: 'Updating mapping with new staff.' });
          try {
            await updateStaffCourseMapping(periodAssignment.id, { staffId, courseCodeId, monthKey });
            patchRow(row.rowNumber, { status: 'success', message: 'Mapping updated with new staff.' });
            success += 1;

            // Keep local indexes in sync for subsequent rows
            const oldFullKey = `${periodAssignment.staffId}-${courseCodeId}-${monthKey}`;
            options.assignmentIndexByFullKey?.delete(oldFullKey);
            const newAssignment = { ...periodAssignment, staffId };
            options.assignmentIndexByFullKey?.set(fullKey, newAssignment);
            options.assignmentIndexByCoursePeriod?.set(coursePeriodKey, newAssignment);
          } catch (error) {
            const serverMessage = error?.response?.data?.message || error.message || 'Failed to update mapping';
            patchRow(row.rowNumber, { status: 'error', message: serverMessage });
            failure += 1;
          }
          continue;
        }

        // Same staff for this course+period; treat as already existing
        patchRow(row.rowNumber, { status: 'skipped', message: 'Already exist data.' });
        continue;
      }

      // No existing mapping found -> create new
      patchRow(row.rowNumber, { status: 'uploading', message: 'Creating mapping...' });

      try {
        await createStaffCourseMapping(row.payload);
        patchRow(row.rowNumber, { status: 'success', message: 'Mapping created.' });
        success += 1;

        // Register new mapping locally if API accepted it; we don’t have its ID,
        // so we only update simple existence set.
        const newFullKey = `${staffId}-${courseCodeId}-${monthKey}`;
        options.existingAssignments?.add(newFullKey);
      } catch (error) {
        const serverMessage = error?.response?.data?.message || error.message || 'Failed to create mapping';
        patchRow(row.rowNumber, { status: 'error', message: serverMessage });
        failure += 1;
      }
    }

    setUploading(false);
    Swal.fire({
      icon: failure ? 'warning' : 'success',
      title: 'Upload finished',
      text: `${success} mapping${success === 1 ? '' : 's'} created, ${failure} failed.`,
      confirmButtonColor: '#0f172a',
    });
  };

  const uploadDisabled = uploading || !readyRows.length;

  if (!hasAdminAccess) {
    return (
      <DashboardLayout title="Upload Staff Course Mapping">
        <div className="rounded-3xl border border-rose-100 bg-rose-50 p-10 text-center">
          <h2 className="text-xl font-semibold text-rose-700">Access restricted</h2>
          <p className="mt-2 text-sm text-rose-600">
            Only admin users can upload staff course mapping templates. Please contact an administrator if you believe this is a mistake.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Upload Staff Course Mapping">
      <div className="space-y-6">
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-800 p-8 text-white shadow-xl">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-3xl space-y-4">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-white/60">Bulk create</p>
                <h1 className="mt-2 text-3xl font-semibold">Upload Staff Course Mapping Template</h1>
                <p className="mt-3 text-sm text-white/80">
                  Download the Staff_Course_Mapping_Template, fill Staff ID, Course Code ID, Assignment Month (1-12), and Assignment Year (YYYY). Each row creates one mapping. Duplicates and unknown IDs are blocked before upload.
                </p>
                <p className="text-xs text-white/70">
                  Staff and course lookup values are validated against the current directory. Existing mappings for the same staff, course, and month are flagged.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={resetWorkspace}
                disabled={!rows.length && !fileMeta}
                className="inline-flex items-center justify-center rounded-xl border border-white/20 px-6 py-3 text-sm font-semibold text-white/80 transition hover:text-white disabled:cursor-not-allowed disabled:border-white/10 disabled:text-white/40"
              >
                Reset workspace
              </button>
              <button
                onClick={loadOptions}
                disabled={optionsLoaded}
                className="inline-flex items-center justify-center rounded-xl border border-white/20 px-6 py-3 text-sm font-semibold text-white/80 transition hover:text-white disabled:cursor-not-allowed disabled:border-white/10 disabled:text-white/40"
              >
                {optionsLoaded ? 'Options loaded' : 'Refresh directories'}
              </button>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
            <p className="text-xs font-semibold text-emerald-600">Rows ready</p>
            <p className="mt-2 text-3xl font-bold text-emerald-800">{readyRows.length}</p>
          </div>
          <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4">
            <p className="text-xs font-semibold text-rose-600">Rows with issues</p>
            <p className="mt-2 text-3xl font-bold text-rose-800">{blockedRows.length}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold text-slate-500">Created</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{successCount}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold text-slate-500">Failed</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{failedCount}</p>
          </div>
        </div>

        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
          className="flex cursor-pointer flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-300 bg-white p-10 text-center transition hover:border-indigo-400 hover:bg-indigo-50"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleFileChange}
          />
          <svg className="h-16 w-16 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="mt-4 text-lg font-semibold text-slate-900">
            {parsing ? 'Analyzing file...' : 'Drop the Excel file or click to browse'}
          </p>
          <p className="mt-2 max-w-xl text-sm text-slate-500">
            Accepts .xlsx, .xls, or .csv files generated from the Staff_Course_Mapping_Template. Formatting, formulas, and colors are ignored during import.
          </p>
          {fileMeta && (
            <div className="mt-4 rounded-full bg-slate-100 px-4 py-1 text-xs text-slate-600">
              Loaded {fileMeta.name} • Sheet: {fileMeta.sheet} • {fileMeta.total} rows
            </div>
          )}
        </div>

        {rows.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-700">Bulk create</p>
                <p className="text-xs text-slate-500">Review the rows and start the upload when ready.</p>
              </div>
              <button
                onClick={handleUpload}
                disabled={uploadDisabled}
                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
              >
                {uploading ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Creating...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4h16v14a2 2 0 01-2 2H6a2 2 0 01-2-2z" />
                    </svg>
                    Start upload
                  </>
                )}
              </button>
            </div>

            <div className="max-h-[520px] overflow-auto">
              <table className="min-w-max divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Row</th>
                    <th className="px-4 py-3">Staff ID</th>
                    <th className="px-4 py-3">Course Code ID</th>
                    <th className="px-4 py-3">Month (1-12)</th>
                    <th className="px-4 py-3">Year</th>
                    <th className="px-4 py-3">Validation</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        className="px-4 py-12 text-center text-sm text-slate-500"
                        colSpan={7}
                      >
                        Upload the staff course mapping template to preview rows.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const statusStyle = STATUS_STYLES[row.status] || STATUS_STYLES.pending;
                      return (
                        <tr key={row.rowNumber} className="align-top">
                          <td className="px-4 py-3 font-mono text-xs text-slate-500">{row.rowNumber}</td>
                          <td className="px-4 py-3 text-slate-700">{row.preview.staffId || '—'}</td>
                          <td className="px-4 py-3 text-slate-700">{row.preview.courseCodeId || '—'}</td>
                          <td className="px-4 py-3 text-slate-700">{row.preview.monthNumber || '—'}</td>
                          <td className="px-4 py-3 text-slate-700">{row.preview.year || '—'}</td>
                          <td className="px-4 py-3">
                            {row.errors.length > 0 ? (
                              <ul className="space-y-1 text-xs text-rose-600">
                                {row.errors.map((error, idx) => (
                                  <li key={idx}>• {error}</li>
                                ))}
                              </ul>
                            ) : (
                              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                                Ready to upload
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${statusStyle}`}>
                              <span>
                                {row.status === 'pending' && 'Pending'}
                                {row.status === 'uploading' && 'Uploading'}
                                {row.status === 'success' && 'Created'}
                                {row.status === 'error' && 'Failed'}
                                {row.status === 'blocked' && 'Blocked'}
                                {row.status === 'skipped' && 'Skipped'}
                              </span>
                            </div>
                            {row.message && (
                              <p className="mt-1 text-xs text-slate-600">{row.message}</p>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
